import { Alert, Button, Skeleton, Stack, Text } from '@chakra-ui/react'
import { PaywallOfferControls } from '@src/components/sco/Conversion/PaywallOfferControls'
import { PaywallPage } from '@src/components/sco/Conversion/PaywallPage'
import { PendingPlanChangeBanner } from '@src/components/sco/Conversion/PendingPlanChangeBanner'
import {
  conversionCheckoutHref,
  PLAN_CHANGE_AT_RENEWAL,
} from '@src/components/sco/Conversion/controller/checkoutParams'
import { useCheckoutCouponCode } from '@src/components/sco/Conversion/controller/useCheckoutCouponCode'
import { useConversionProducts } from '@src/components/sco/Conversion/controller/useConversionProducts'
import { useMembershipPlanChange } from '@src/components/sco/Conversion/controller/useMembershipPlanChange'
import {
  ADVISOR_TEAM_PRODUCT_ID,
  STARTUPS_PRO_PRODUCT_ID,
  type PaywallIntentId,
} from '@src/components/sco/Conversion/intent'
import { membershipTermOptions, productPricedAtTerm } from '@src/components/sco/Conversion/offers'
import { FREE_PLAN_ID, freeMembershipProduct } from '@src/components/sco/Conversion/planFeatures'
import { productLabel } from '@src/components/sco/Conversion/presentation'
import { heldTermByProductId, memberDefaultTermId } from '@src/components/sco/UpgradeForm'
import { useCurrentUser } from '@src/hooks/useCurrentUser'
import { startupsPath } from '@src/lib/startups-url'
import { usePathname, useSearchParams } from 'next/navigation'
import { useRouter } from 'next/router'
import { useMemo, useState, type ReactNode } from 'react'

const LADDER = [STARTUPS_PRO_PRODUCT_ID, ADVISOR_TEAM_PRODUCT_ID]

const MEMBERSHIP_ANSWERS = [
  {
    question: 'Can I cancel anytime?',
    answer:
      'Yes. Memberships renew at the end of each billing period until you cancel from your billing page. You keep access through the period you paid for.',
  },
  {
    question: 'Can I move from Startups Pro to Advisor Team later?',
    answer:
      'Yes. The change applies to your existing membership: you see the prorated amount due today before you confirm, then renew at the Advisor Team price.',
  },
  {
    question: 'Can I move from Advisor Team to Startups Pro?',
    answer:
      'Yes. The switch applies at your next renewal. You keep Advisor Team until then, nothing is refunded, and your Team advisor seats end at the change. You can keep Team any time before that date.',
  },
  {
    question: 'Can I change my billing period?',
    answer:
      'Yes. On the plan you already have, choose another period. A longer period starts now: you see the prorated amount due today before you confirm. A shorter period starts at your next renewal: nothing is charged or credited now, you keep your current period until then, and you can keep your current plan any time before that date.',
  },
  {
    question: 'Are paid advisor sessions included?',
    answer:
      'No. Sessions are booked and paid separately at the rate each advisor sets, on any plan, including a free account.',
  },
]

export type MembershipPricingPageProps = {
  intent?: PaywallIntentId
  /** Off when the page already titles the plans section (e.g. /accelerator/start). */
  showHero?: boolean
  /** Adds the free account as the first plan. Choosing it opens registration, not checkout. */
  showFree?: boolean
  align?: 'start' | 'center'
  /** A secondary way forward inside the closing offer, e.g. "Book a call". */
  closingExtra?: ReactNode
  /** Off hides the billing-term picker; prices stay on `?termId=` (a member's held term, else monthly). */
  showTerms?: boolean
  /** Off hides the promotion code field. A code from the link, cookie, or account still applies. */
  showPromotionCode?: boolean
}

/**
 * Membership pricing on the Conversion kit: catalog prices per billing term (savings computed
 * from those prices), promotion codes the API resolves, plan cards, a compare, and answers.
 * Choosing a plan opens the Conversion checkout for that product, term, and applied code.
 */
export const MembershipPricingPage = ({
  align = 'start',
  closingExtra,
  intent = 'default',
  showFree = false,
  showHero = true,
  showPromotionCode = true,
  showTerms = true,
}: MembershipPricingPageProps) => {
  const router = useRouter()
  const pathname = usePathname()
  const params = useSearchParams()
  const { currentUser } = useCurrentUser()
  const { applyCouponCode, couponCode, couponFieldActive, removeCouponCode } = useCheckoutCouponCode()
  // The picker or `?termId=` is an explicit choice. Otherwise a member sees the term they hold, so
  // a higher plan is an immediate prorated upgrade, never a shorter term scheduled for renewal.
  const [pickedTermId, setTermId] = useState<string | null>(params?.get('termId') || null)
  const termId = pickedTermId || memberDefaultTermId(currentUser) || 'month'

  const { products, loading, refetch } = useConversionProducts(LADDER, { termId, couponCode })
  // Each period is its own query. Hold the last priced plans so a term change does not unmount
  // the offer into the first-load skeleton. Prices for the selected period come from catalog
  // terms already on those plans until that query returns.
  const [heldProducts, setHeldProducts] = useState(products)
  if (products.length > 0 && heldProducts !== products) {
    setHeldProducts(products)
  }
  const stableProducts = products.length > 0 ? products : heldProducts
  const viewedProducts = useMemo(
    () => stableProducts.map(product => productPricedAtTerm(product, termId)),
    [stableProducts, termId]
  )
  const terms = useMemo(() => membershipTermOptions(stableProducts), [stableProducts])
  const appliedCouponCode =
    stableProducts.find(product => product.appliedCoupon?.code)?.appliedCoupon?.code || null
  const couponError =
    couponFieldActive && !loading && products.length && !appliedCouponCode
      ? `That code doesn't apply to ${products.map(productLabel).join(' or ')}.`
      : null

  const activeLadder = useMemo(
    () =>
      (currentUser?.subscriptions || [])
        .filter(subscription => subscription?.isActive)
        .map(subscription => subscription?.productId || subscription?.product?.productId || '')
        .filter(productId => LADDER.includes(productId)),
    [currentUser?.subscriptions]
  )
  const onTeam = activeLadder.includes(ADVISOR_TEAM_PRODUCT_ID)
  // Team members may switch to Pro at renewal (W4); any member may switch to a shorter billing
  // term at renewal (L57). A change already scheduled shows its banner.
  const planChange = useMembershipPlanChange({ skip: activeLadder.length === 0 })
  const signedIn = Boolean(currentUser?.isRegistered)
  const freeIsCurrent = showFree && signedIn && activeLadder.length === 0
  const currentProductIds = useMemo(
    () => (onTeam ? [ADVISOR_TEAM_PRODUCT_ID] : freeIsCurrent ? [FREE_PLAN_ID] : activeLadder),
    [activeLadder, freeIsCurrent, onTeam]
  )
  const currentTermByProductId = useMemo(
    () => heldTermByProductId(currentUser, currentProductIds, termId),
    [currentProductIds, currentUser, termId]
  )
  const listedProducts = useMemo(
    () => (showFree ? [freeMembershipProduct(), ...viewedProducts] : viewedProducts),
    [showFree, viewedProducts]
  )

  if (stableProducts.length === 0 && loading) {
    return (
      <Stack aria-label={'Loading plans'} gap={6} w={'full'}>
        <Skeleton h={10} w={'60%'} />
        <Skeleton h={96} w={'full'} />
      </Stack>
    )
  }

  if (stableProducts.length === 0) {
    return (
      <Alert.Root status={'error'}>
        <Alert.Indicator />
        <Stack align={'start'} gap={2}>
          <Text>Plans aren’t available right now.</Text>
          <Button size={'sm'} variant={'outline'} onClick={() => refetch()}>
            Try again
          </Button>
        </Stack>
      </Alert.Root>
    )
  }

  const freeCta = showFree ? { [FREE_PLAN_ID]: 'Create a free account' } : undefined
  const ctaLabels = onTeam ? planChange.ctaLabels : freeCta
  const eligibleProductIds = onTeam
    ? planChange.offerProductIds
    : [
        ...LADDER.filter(productId => !activeLadder.includes(productId)),
        ...(showFree && !signedIn ? [FREE_PLAN_ID] : []),
      ]

  return (
    <Stack w={'full'}>
      {planChange.pending ? (
        <PendingPlanChangeBanner
          error={planChange.keepError}
          keeping={planChange.keeping}
          pending={planChange.pending}
          onKeep={() => planChange.keepCurrentPlan()}
        />
      ) : null}
      <PaywallPage
        align={align}
        answers={MEMBERSHIP_ANSWERS}
        assurance={'Cancel anytime'}
        closingExtra={closingExtra}
        ctaLabels={ctaLabels}
        currentProductIds={currentProductIds}
        currentTermByProductId={currentTermByProductId}
        eligibleProductIds={eligibleProductIds}
        intent={intent}
        productOrder={showFree ? 'given' : 'recommended'}
        products={listedProducts}
        showHero={showHero}
        cardsFooter={
          showPromotionCode ? (
            <PaywallOfferControls
              align={'center'}
              appliedCouponCode={appliedCouponCode}
              couponError={couponError}
              couponIsApplying={couponFieldActive && loading}
              selectedTermId={termId}
              terms={[]}
              onApplyCoupon={applyCouponCode}
              onRemoveCoupon={removeCouponCode}
              onTermChange={setTermId}
            />
          ) : undefined
        }
        // The term picker prices every card, so it leads; the code field is for the few who have one.
        controls={
          showTerms && terms.length > 1 ? (
            <PaywallOfferControls
              align={align}
              selectedTermId={termId}
              terms={terms}
              onTermChange={setTermId}
            />
          ) : undefined
        }
        onContinue={product => {
          if (!product.productId) {
            return
          }
          if (product.productId === FREE_PLAN_ID) {
            router.push(startupsPath(pathname || '/accelerator/plans', { prompt: 'register' }), undefined, {
              scroll: false,
              shallow: true,
            })
            return
          }
          const switchAtRenewal = planChange.offerProductIds.includes(product.productId)
          router.push(
            conversionCheckoutHref(pathname || '/accelerator/plans', params?.toString(), {
              productId: product.productId,
              intent,
              termId,
              couponCode: product.appliedCoupon?.code || null,
              planChange: switchAtRenewal ? PLAN_CHANGE_AT_RENEWAL : null,
            }),
            undefined,
            { scroll: false, shallow: true }
          )
        }}
      />
    </Stack>
  )
}
